import React from 'react';
import { Clock, MapPin, MoreVertical, Calendar as CalendarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { DS } from '../../lib/design-system';

interface Event {
  id: string;
  title: string;
  time: string;
  location: string;
  date: string;
}

interface DayEventsSectionProps {
  selectedDate: Date;
  events: Event[];
  onEventClick?: (eventId: string) => void;
  onAddEvent?: () => void;
}

export const DayEventsSection: React.FC<DayEventsSectionProps> = ({
  selectedDate,
  events,
  onEventClick,
  onAddEvent,
}) => {
  const monthName = selectedDate.toLocaleString('es-ES', { month: 'short' }).toUpperCase();
  const dayNumber = selectedDate.getDate();

  const isToday = () => {
    const today = new Date();
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  };

  return (
    <div className="bg-gray-50 px-4 py-4">
      {/* Section Header */}
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
        EVENTOS DEL DÍA
      </h4>

      {/* Events List */}
      {events.length > 0 ? (
        <div className="space-y-3">
          {events.map((event) => (
            <motion.div
              key={event.id}
              onClick={() => onEventClick?.(event.id)}
              className="bg-white rounded-xl p-4 shadow-sm relative border-l-4 border-[#C9A961] cursor-pointer hover:shadow-md transition-shadow"
              whileHover={DS.animations.cardHover}
              whileTap={DS.animations.cardTap}
            >
              <div className="flex gap-4">
                {/* Date Badge */}
                <div className="text-center flex-shrink-0">
                  <div className="text-xs font-bold text-gray-400 uppercase">
                    {monthName}
                  </div>
                  <div className="text-2xl font-black text-gray-800 leading-none">
                    {dayNumber}
                  </div>
                </div>

                {/* Event Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-base mb-2 leading-tight">
                    {event.title}
                  </h3>

                  {/* Time */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Clock size={14} className="flex-shrink-0" />
                    <span>{event.time}</span>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin size={14} className="flex-shrink-0" />
                    <span className="truncate">{event.location}</span>
                  </div>
                </div>

                {/* Menu Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle menu click
                  }}
                  className="self-start p-1.5 text-gray-300 hover:text-[#5D1826] hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <MoreVertical size={18} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 text-gray-400">
            <CalendarIcon size={24} />
          </div>
          <p className="text-gray-500 font-medium mb-1">
            {isToday() ? 'No hay más eventos hoy' : 'No hay eventos para este día'}
          </p>
          <button
            onClick={onAddEvent}
            className="text-[#C9A961] text-sm font-semibold hover:underline"
          >
            Programar actividad
          </button>
        </div>
      )}
    </div>
  );
};
